import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFarm } from "../context/FarmContext";
import { api } from "../lib/api";
import { LIVESTOCK, livestockById, BIRD_CATEGORIES, addDays } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Trash2, ClipboardList, Users, Pencil, LineChart, Sprout } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const empty = { activity_type: "", batch_id: "", date: new Date().toISOString().slice(0, 10), quantity: 0, unit: "", cost: 0, unit_price: 0, notes: "" };
const herdEmpty = () => ({ id: null, livestock_type: "cattle", label: "", count: 0, status: "active", sale_amount: 0, category: "", hatch_date: "", lay_days: 0, meat_days: 0, price_per_bird: 0, date: new Date().toISOString().slice(0, 10), notes: "" });

function BirdFields({ form, setForm, fmt, symbol }) {
  const pick = (id) => {
    const c = BIRD_CATEGORIES.find((b) => b.id === id);
    setForm({ ...form, category: id, lay_days: c.lay_days, meat_days: c.meat_days });
  };
  const num = (k) => (e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 });
  const meatDate = addDays(form.hatch_date, form.meat_days);
  return (
    <>
      <div className="col-span-2">
        <Label>Bird Category</Label>
        <Select value={form.category} onValueChange={pick}>
          <SelectTrigger className="mt-1.5" data-testid="headcount-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{BIRD_CATEGORIES.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} — {b.note}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label>Hatch / Arrival Date</Label>
        <Input type="date" className="mt-1.5" value={form.hatch_date} onChange={(e) => setForm({ ...form, hatch_date: e.target.value })} data-testid="headcount-hatch-input" />
      </div>
      <div>
        <Label>Days to start laying</Label>
        <Input type="number" className="mt-1.5" value={form.lay_days} onChange={num("lay_days")} data-testid="headcount-lay-days-input" />
      </div>
      <div>
        <Label>Days to meat-ready</Label>
        <Input type="number" className="mt-1.5" value={form.meat_days} onChange={num("meat_days")} data-testid="headcount-meat-days-input" />
      </div>
      <div className="col-span-2">
        <Label>Expected meat price per bird ({symbol})</Label>
        <Input type="number" step="0.01" className="mt-1.5" value={form.price_per_bird} onChange={num("price_per_bird")} data-testid="headcount-price-input" />
        {form.hatch_date && (
          <p className="text-xs mt-1.5 text-muted-foreground" data-testid="headcount-timing-preview">
            {form.lay_days ? `Starts laying ${addDays(form.hatch_date, form.lay_days)}. ` : ""}
            {meatDate ? `Meat-ready ${meatDate}.` : ""}
            {meatDate && form.price_per_bird > 0 && form.count > 0 && <span className="text-[hsl(var(--emerald))]"> Adds {fmt(form.count * form.price_per_bird)} to Expected Income.</span>}
          </p>
        )}
      </div>
    </>
  );
}

export default function Activities() {
  const { settings, fmt, reload } = useFarm();
  const [filter, setFilter] = useState(settings.active_livestock);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...empty, livestock_type: settings.active_livestock, unit_price: settings.last_prices?.[settings.active_livestock] || 0 });
  const activeCfg = livestockById(form.livestock_type);

  const [herd, setHerd] = useState([]);
  const [herdOpen, setHerdOpen] = useState(false);
  const [herdForm, setHerdForm] = useState(herdEmpty());
  const navigate = useNavigate();

  const load = () => api.getActivities().then((all) => setItems(filter === "all" ? all : all.filter((a) => a.livestock_type === filter)));
  const loadHerd = () => api.getHerd(filter === "all" ? undefined : filter).then(setHerd);
  useEffect(() => { load(); loadHerd(); }, [filter]);
  useEffect(() => { setFilter(settings.active_livestock); }, [settings.active_livestock]);

  const openHerd = (entry) => {
    setHerdForm(entry ? { ...entry } : { ...herdEmpty(), livestock_type: filter === "all" ? settings.active_livestock : filter });
    setHerdOpen(true);
  };
  const saveHerd = async () => {
    if (!herdForm.livestock_type || !herdForm.count) return toast.error("Type and count are required");
    if (herdForm.id) await api.updateHerd(herdForm.id, herdForm);
    else await api.createHerd(herdForm);
    toast.success("Headcount saved");
    setHerdOpen(false); setHerdForm(herdEmpty()); loadHerd();
  };
  const removeHerd = async (id) => { await api.deleteHerd(id); loadHerd(); toast.success("Removed"); };
  const totalHead = herd.filter((h) => h.status === "active").reduce((s, h) => s + (h.count || 0), 0);
  const historyData = (() => {
    const map = {};
    herd.forEach((h) => {
      const m = (h.date || "").slice(0, 7);
      if (!m) return;
      map[m] = map[m] || { month: m, added: 0, removed: 0 };
      if (h.status === "active") map[m].added += h.count || 0;
      else map[m].removed += h.count || 0;
    });
    let running = 0;
    return Object.keys(map).sort().map((m) => {
      running += map[m].added - map[m].removed;
      return { label: new Date(m + "-01").toLocaleDateString(undefined, { month: "short", year: "2-digit" }), headcount: Math.max(running, 0) };
    });
  })();
  const quickLog = (activityType) => {
    const lt = filter === "all" ? settings.active_livestock : filter;
    setForm({ ...empty, livestock_type: lt, activity_type: activityType, unit_price: settings.last_prices?.[lt] || 0 });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.activity_type) return toast.error("Select an activity type");
    await api.createActivity(form);
    await reload?.();
    toast.success("Activity recorded");
    setOpen(false);
    setForm({ ...empty, livestock_type: settings.active_livestock, unit_price: settings.last_prices?.[settings.active_livestock] || 0 });
    load();
  };
  const remove = async (id) => { await api.deleteActivity(id); load(); toast.success("Deleted"); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid="activities-title">{filter === "all" ? "Farm Activities" : `${livestockById(filter).name} — ${livestockById(filter).register}`}</h1>
          <p className="text-sm text-muted-foreground mt-1">Record your headcount and daily farm operations.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px] h-10" data-testid="activity-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Livestock</SelectItem>
              {LIVESTOCK.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm((f) => ({ ...f, unit_price: settings.last_prices?.[f.livestock_type] || 0 })); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-activity-button" className="h-10 rounded-full gap-1.5">
                <Plus className="h-4 w-4" /> Record Activity
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Record Activity</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2">
                  <Label>Livestock</Label>
                  <Select value={form.livestock_type} onValueChange={(v) => setForm({ ...form, livestock_type: v, activity_type: "", unit_price: settings.last_prices?.[v] || 0 })}>
                    <SelectTrigger className="mt-1.5" data-testid="activity-livestock-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LIVESTOCK.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Activity Type</Label>
                  <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="activity-type-select"><SelectValue placeholder="Select activity" /></SelectTrigger>
                    <SelectContent>{activeCfg.activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="activity-date-input" />
                </div>
                <div>
                  <Label>Batch / Tag ID</Label>
                  <Input className="mt-1.5" value={form.batch_id} onChange={(e) => setForm({ ...form, batch_id: e.target.value })} placeholder="e.g. B-12" data-testid="activity-batch-input" />
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" className="mt-1.5" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} data-testid="activity-quantity-input" />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input className="mt-1.5" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="L, kg, crates" data-testid="activity-unit-input" />
                </div>
                <div className="col-span-2">
                  <Label>Cost ({settings.currency_symbol})</Label>
                  <Input type="number" className="mt-1.5" value={form.cost} onChange={(e) => setForm({ ...form, cost: parseFloat(e.target.value) || 0 })} data-testid="activity-cost-input" />
                </div>
                <div className="col-span-2">
                  <Label>Sale Price per Unit ({settings.currency_symbol}) — optional</Label>
                  <Input type="number" className="mt-1.5" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })} placeholder="Records expected income from this produce" data-testid="activity-price-input" />
                  {form.quantity > 0 && form.unit_price > 0 && (
                    <p className="mt-1.5 text-xs text-[hsl(var(--emerald))]" data-testid="expected-income-preview">
                      Adds {fmt(form.quantity * form.unit_price)} to Expected Income
                    </p>
                  )}
                  {settings.last_prices?.[form.livestock_type] > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="price-memory-hint">Auto-filled from your last {form.livestock_type} sale.</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="activity-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="rounded-full" data-testid="save-activity-button">Save Activity</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {filter === "plants" ? (
        <Card className="p-5" data-testid="planting-shortcut">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2"><Sprout className="h-5 w-5 text-[hsl(var(--emerald))]" /> Plantings</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Track planted crops, yields and harvest dates on the Crops page.</p>
            </div>
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => navigate("/crops")} data-testid="record-planting-button"><Plus className="h-4 w-4" /> Record Planting</Button>
          </div>
        </Card>
      ) : (
      <Card className="p-5" data-testid="headcount-register">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
              <Users className="h-5 w-5 text-accent" />
              {filter === "all" ? "Herd & Flock Register" : `${livestockById(filter).register} Register`}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Record how many animals you keep, by breed or batch.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Active {filter === "all" ? "animals" : livestockById(filter).register}</p>
              <p className="font-display text-2xl font-extrabold font-mono-nums" data-testid="headcount-total">{totalHead.toLocaleString()}</p>
            </div>
            <Button size="sm" className="rounded-full gap-1.5" onClick={() => openHerd(null)} data-testid="add-headcount-button"><Plus className="h-4 w-4" /> Record Headcount</Button>
          </div>
        </div>
        {herd.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="headcount-table">
              <TableHeader><TableRow>
                <TableHead>Breed / Batch</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Lays / Meat-ready</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Sale</TableHead>
                <TableHead>Date</TableHead>
                <TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {herd.map((h) => (
                  <TableRow key={h.id} data-testid={`headcount-row-${h.id}`}>
                    <TableCell className="font-medium">{h.label || "—"}{h.notes && <span className="block text-xs text-muted-foreground">{h.notes}</span>}</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{h.category || h.livestock_type}</Badge></TableCell>
                    <TableCell className="text-xs font-mono-nums whitespace-nowrap" data-testid={`headcount-timing-${h.id}`}>
                      {h.hatch_date ? (<>
                        <span className="block">Lays: {h.lay_days ? addDays(h.hatch_date, h.lay_days) : "—"}</span>
                        <span className="block text-[hsl(var(--emerald))]">Meat: {h.meat_days ? addDays(h.hatch_date, h.meat_days) : "—"}{h.meat_tx_id ? ` · ${fmt(h.count * h.price_per_bird)}` : ""}</span>
                      </>) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-nums font-semibold">{h.count.toLocaleString()}</TableCell>
                    <TableCell><Badge variant={h.status === "active" ? "default" : "secondary"} className="capitalize">{h.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono-nums text-[hsl(var(--emerald))]">{h.status === "sold" && h.sale_amount ? fmt(h.sale_amount) : "—"}</TableCell>
                    <TableCell className="font-mono-nums text-sm">{h.date}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openHerd(h)} data-testid={`edit-headcount-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeHerd(h.id)} data-testid={`delete-headcount-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">No headcount recorded yet. Add your {filter === "all" ? "animals" : livestockById(filter).register.toLowerCase()} to see totals.</p>
        )}
      </Card>
      )}

      {filter !== "plants" && historyData.length > 0 && (
        <Card className="p-6" data-testid="headcount-history">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4 flex items-center gap-2"><LineChart className="h-5 w-5 text-accent" /> Headcount History</h2>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={historyData} margin={{ left: -12, right: 8 }}>
              <defs>
                <linearGradient id="hc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
              <Tooltip formatter={(v) => [v, "Headcount"]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 13 }} />
              <Area type="monotone" dataKey="headcount" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#hc)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {filter !== "all" && (
        <div className="flex flex-wrap items-center gap-2" data-testid="quick-log-chips">
          <span className="text-xs text-muted-foreground mr-1">Quick log:</span>
          {livestockById(filter).activities.map((a) => (
            <Button key={a} variant="outline" size="sm" className="rounded-full h-8" onClick={() => quickLog(a)} data-testid={`quick-log-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{a}</Button>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        {items.length ? (
          <div className="overflow-x-auto">
            <Table data-testid="activities-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Livestock</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} data-testid={`activity-row-${a.id}`}>
                    <TableCell className="font-mono-nums text-sm">{a.date}</TableCell>
                    <TableCell className="font-medium">{a.activity_type}{a.notes && <span className="block text-xs text-muted-foreground">{a.notes}</span>}</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{a.livestock_type}</Badge></TableCell>
                    <TableCell className="font-mono-nums text-sm">{a.batch_id || "—"}</TableCell>
                    <TableCell className="text-right font-mono-nums">{a.quantity ? `${a.quantity} ${a.unit}` : "—"}</TableCell>
                    <TableCell className="text-right font-mono-nums">{a.cost ? fmt(a.cost) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => remove(a.id)} data-testid={`delete-activity-${a.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="py-20 text-center">
            <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">No activities logged yet.</p>
          </div>
        )}
      </Card>

      <Dialog open={herdOpen} onOpenChange={setHerdOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{herdForm.id ? "Edit" : "Record"} Headcount</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Livestock Type</Label>
              <Select value={herdForm.livestock_type} onValueChange={(v) => setHerdForm({ ...herdForm, livestock_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="headcount-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LIVESTOCK.filter((l) => l.id !== "plants").map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Breed / Batch</Label>
              <Input className="mt-1.5" value={herdForm.label} onChange={(e) => setHerdForm({ ...herdForm, label: e.target.value })} placeholder="e.g. Broilers A" data-testid="headcount-label-input" />
            </div>
            <div>
              <Label>Count</Label>
              <Input type="number" className="mt-1.5" value={herdForm.count} onChange={(e) => setHerdForm({ ...herdForm, count: parseInt(e.target.value) || 0 })} data-testid="headcount-count-input" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={herdForm.status} onValueChange={(v) => setHerdForm({ ...herdForm, status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="headcount-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" className="mt-1.5" value={herdForm.date} onChange={(e) => setHerdForm({ ...herdForm, date: e.target.value })} data-testid="headcount-date-input" />
            </div>
            {herdForm.livestock_type === "poultry" && <BirdFields form={herdForm} setForm={setHerdForm} fmt={fmt} symbol={settings.currency_symbol} />}
            {herdForm.status === "sold" && (
              <div className="col-span-2">
                <Label>Sale Amount ({settings.currency_symbol})</Label>
                <Input type="number" step="0.01" className="mt-1.5" value={herdForm.sale_amount} onChange={(e) => setHerdForm({ ...herdForm, sale_amount: parseFloat(e.target.value) || 0 })} data-testid="headcount-sale-input" />
                <p className="text-xs text-[hsl(var(--emerald))] mt-1" data-testid="headcount-sale-hint">Records {fmt(herdForm.sale_amount || 0)} as income.</p>
              </div>
            )}
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input className="mt-1.5" value={herdForm.notes} onChange={(e) => setHerdForm({ ...herdForm, notes: e.target.value })} data-testid="headcount-notes-input" />
            </div>
          </div>
          <DialogFooter><Button className="rounded-full" onClick={saveHerd} data-testid="save-headcount-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
